import { useCallback, useMemo, useState } from "react";

import { styled } from "@linaria/react";

import FlexItem from "@gx/dls/containers/FlexItem";
import FlexWrapper, { AlignItems, JustifyContent } from "@gx/dls/containers/FlexWrapper";
import HorizontalDivider from "@gx/dls/dividers/HorizontalDivider";
import Text, { TextSize, TextVariant } from "@gx/dls/text/Text";
import { withTheme, type PropsWithTheme } from "@gx/dls/theme";

import type { FileUploadProgress } from "@/pages/sources/components/create/sources/types";
import DropzoneWidgetTable from "@/pages/sources/components/dropzone/DropzoneWidgetTable";
import DropzoneWidgetToolbar from "@/pages/sources/components/dropzone/DropzoneWidgetToolbar";

const DropzoneWidgetExplorerWrapper = withTheme(styled.div<PropsWithTheme>`
  display: flex;
  flex-direction: column;
  width: 100%;
  flex: 1;
  min-height: 0;
  max-height: 340px;
  border-radius: 5px;
  background-color: ${({ theme }) => theme.color.background.primary};
  border: 0.5px solid ${({ theme }) => theme.color.border.primary};
`);

export interface DropzoneWidgetProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
  fileProgressMap?: Map<string, FileUploadProgress>;
  isDisabled?: boolean;
}

interface DropzoneWidgetState {
  search: string;
}

const DEFAULT_STATE: DropzoneWidgetState = {
  search: "",
};

const DropzoneWidgetExplorer = ({
  files,
  onFilesChange,
  fileProgressMap,
  isDisabled = false,
}: DropzoneWidgetProps) => {
  const [state, setState] = useState<DropzoneWidgetState>(DEFAULT_STATE);

  const handleSearchChange = useCallback((value: string) => {
    setState((prev) => ({
      ...prev,
      search: value,
    }));
  }, []);

  const handleRemoveFile = useCallback(
    (indexToRemove: number) => {
      onFilesChange(files.filter((_, index) => index !== indexToRemove));
    },
    [files, onFilesChange],
  );

  const filteredFiles = useMemo(() => {
    if (!state.search.trim()) {
      return files;
    }
    const query = state.search.toLowerCase();
    return files.filter((file) => file.name.toLowerCase().includes(query));
  }, [files, state.search]);

  return (
    <DropzoneWidgetExplorerWrapper>
      <FlexWrapper padding={"8px"} fillWidth>
        <DropzoneWidgetToolbar
          search={state.search}
          onSearchChange={handleSearchChange}
          numFiles={files.length}
          isDisabled={isDisabled}
        />
      </FlexWrapper>
      <FlexItem>
        <HorizontalDivider />
      </FlexItem>
      {filteredFiles.length > 0 ? (
        <DropzoneWidgetTable
          files={filteredFiles}
          onRemoveFile={handleRemoveFile}
          isDisabled={isDisabled}
          fileProgressMap={fileProgressMap}
        />
      ) : (
        <FlexWrapper
          alignItems={AlignItems.CENTER}
          justifyContent={JustifyContent.CENTER}
          minHeight={66}
        >
          <Text size={TextSize.BODY_SM} variant={TextVariant.DISABLED}>
            No files added
          </Text>
        </FlexWrapper>
      )}
    </DropzoneWidgetExplorerWrapper>
  );
};

export default DropzoneWidgetExplorer;
